// Client-side Bugsnag bootstrap. Imported once from main.tsx before the
// React tree mounts so window.onerror + unhandledrejection hooks fire
// before any app code runs.
//
// The API key is a public project identifier — Bugsnag's browser
// notifier sends from the user's machine, so the key is visible in
// network traffic either way. No secret to protect here.

import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import React from "react";

const API_KEY = "696f8a93c9cf977e51f8cb09955136d0";

// Vite sets import.meta.env.MODE to "development" during `pnpm dev` and
// "production" in built bundles. Skipping init in dev keeps noisy
// hot-reload errors out of the Bugsnag project.
const isProdBundle = import.meta.env.MODE === "production";

// VITE_APP_ENV is wired in the GitHub Actions deploy workflows
// (staging / production) so the release stage lines up with the
// environment even when bundles are identical. Falls back to "staging"
// when missing so locally-served prod builds stay off the production
// dashboard.
const releaseStage = (import.meta.env.VITE_APP_ENV as string | undefined) ?? "staging";

// VITE_APP_VERSION is set to the commit SHA in CI. Matching this value
// against the source-maps upload lets Bugsnag stack-traces de-minify.
const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev";

let errorBoundary: React.ComponentType<{ children?: React.ReactNode }> | null = null;

if (isProdBundle) {
  Bugsnag.start({
    apiKey: API_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage,
    appVersion,
    enabledReleaseStages: ["production", "staging"],
  });
  BugsnagPerformance.start({ apiKey: API_KEY, releaseStage, appVersion });

  const plugin = Bugsnag.getPlugin("react");
  if (plugin) {
    errorBoundary = plugin.createErrorBoundary(React) as React.ComponentType<{
      children?: React.ReactNode;
    }>;
  }
}

/** Error boundary component. In dev (where Bugsnag is disabled) this is
 *  a pass-through so the React tree renders identically. */
export const BugsnagErrorBoundary: React.ComponentType<{ children?: React.ReactNode }> =
  errorBoundary ?? (({ children }) => React.createElement(React.Fragment, null, children));

/** Escape hatch for reporting a handled error with extra metadata. Used
 *  sparingly — most errors reach Bugsnag via the global hooks. */
export function reportHandledError(err: unknown, metadata?: Record<string, unknown>): void {
  if (!isProdBundle) return;
  Bugsnag.notify(err as Error, (event) => {
    if (metadata) {
      event.addMetadata("context", metadata);
    }
  });
}
